import { DialogFeedbackDescriptor, IconButton } from "@appretio/ui-kit";
import { useEffect, useRef } from "react";
import { FeedbackOpenDialog, useFeedbackApi } from "../../FeedbackContext";

/**
 * The report trigger ui-kit's Dialog renders in its title row, and the
 * registration of that dialog's capture block. One component does both so
 * the block lives exactly as long as the dialog: ui-kit cannot call the
 * registration hook itself without depending on this package.
 *
 * Opens the modal with no prefill, like the header button — a dialog says
 * nothing about whether the report is bad data, a bug or an idea.
 */
export function DialogFeedback({
  name,
  title,
  size,
  getContext
}: DialogFeedbackDescriptor) {
  const api = useFeedbackApi();
  const registerOpenDialog = api?.registerOpenDialog;

  // One stable entry object per mount: its identity is the Set key that
  // unregisters cleanly, while its fields track the latest render — dialog
  // titles are often dynamic, and re-registering per render would churn.
  const entry = useRef<FeedbackOpenDialog | null>(null);
  entry.current ??= { opened_at: new Date().toISOString() };
  entry.current.name = name;
  entry.current.title = title;
  entry.current.size = size;
  entry.current.getContext = getContext;

  useEffect(() => {
    if (!registerOpenDialog || !entry.current) return;
    return registerOpenDialog(entry.current);
  }, [registerOpenDialog]);

  return (
    <IconButton
      iconName="megaphone"
      variant="tertiary"
      size="md"
      aria-label="Send feedback about this dialog"
      title="Send feedback about this dialog"
      onClick={() => api?.open()}
    />
  );
}
